/**
 * Estimating 2 visual language (per the module spec): navy chrome, orange
 * accent, light slate work surfaces, white cards, Arial/Helvetica UI text,
 * monospace prices.
 */
export const FONT = { fontFamily: "Arial, Helvetica, sans-serif" };

export const BTN_ORANGE =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#f97316] px-3.5 py-2 text-xs font-semibold uppercase tracking-wider text-white shadow-sm transition-colors hover:bg-[#fb923c] disabled:cursor-not-allowed disabled:opacity-40";
export const BTN_NAVY =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#0b172a] px-3.5 py-2 text-xs font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#16263f] disabled:opacity-40";
export const BTN_GHOST =
  "inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40";
export const BTN_HEAD =
  "inline-flex items-center gap-1.5 rounded-md border border-black/15 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-700 transition-colors hover:bg-black/5 hover:text-slate-900 disabled:opacity-40 dark:border-white/15 dark:text-white/80 dark:hover:bg-white/10 dark:hover:text-white";
export const INPUT =
  "h-9 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-500 focus:border-[#f97316] focus:outline-none focus:ring-1 focus:ring-[#f97316]";
export const INPUT_SM =
  "h-8 w-full rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-900 focus:border-[#f97316] focus:outline-none focus:ring-1 focus:ring-[#f97316]";
export const LABEL = "mb-1 block text-[10px] font-semibold uppercase tracking-widest text-slate-500";
export const CARD = "rounded-xl border border-slate-200 bg-white shadow-sm";
export const MONO = "font-mono tabular-nums";

export const STATUS_STYLES = {
  "Not Set": "bg-slate-100 text-slate-600 border-slate-300",
  "Nothing for Us": "bg-zinc-200 text-zinc-600 border-zinc-400",
  "Bidding": "bg-blue-50 text-blue-700 border-blue-200",
  "Bid Submitted": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Pending Bid Results": "bg-amber-50 text-amber-700 border-amber-200",
  "Won": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Lost": "bg-red-50 text-red-700 border-red-200",
  "In Progress": "bg-orange-50 text-orange-700 border-orange-200",
  "On Hold": "bg-yellow-50 text-yellow-700 border-yellow-300",
  "Completed": "bg-green-50 text-green-800 border-green-200",
  "Awaiting Approval": "bg-purple-50 text-purple-700 border-purple-200",
  "Awaiting Additional Information": "bg-cyan-50 text-cyan-700 border-cyan-200",
  "Project Declined": "bg-rose-50 text-rose-700 border-rose-200",
  "Project Requires Approval": "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
};

export const StatusBadge = ({ status }) => (
  <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLES[status] || STATUS_STYLES["Not Set"]}`}>
    {status || "Not Set"}
  </span>
);

/** Accepted upload extensions (spec list). */
export const ACCEPT_EXT = ".pdf,.png,.jpg,.jpeg,.webp,.gif,.bmp,.svg,.tif,.tiff,.heic,.heif,.avif,.jp2,.dwg,.dxf,.dwf,.rvt,.ifc,.skp,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.rar,.7z";

export const PREVIEWABLE = new Set(["pdf", "png", "jpg", "jpeg", "webp", "gif", "bmp", "tif", "tiff"]);
export const CAD_NOTE = new Set(["dwg", "dxf", "dwf", "rvt", "ifc", "skp"]);

// "plan.dwg (1)" (phone duplicate downloads) still reads as dwg
export const fileExt = (name) => (name || "").replace(/\s*\(\d+\)$/, "").split(".").pop().toLowerCase();
// CAD files are never "photos" — they show through the CAD pipeline (rendered PDF) or the converting card
export const isPreviewable = (d) => !["dwg", "dxf"].includes(fileExt(d?.name)) && (PREVIEWABLE.has(fileExt(d?.name)) || (d?.mime || "").startsWith("image/"));
export const isPdf = (d) => fileExt(d?.name) === "pdf" || d?.mime === "application/pdf";
export const isTiff = (d) => d?.mime === "image/tiff" || ["tif", "tiff"].includes(fileExt(d?.name));
export const isImage = (d) => !isPdf(d) && !isTiff(d) && ((d?.mime || "").startsWith("image/") || ["png", "jpg", "jpeg", "webp", "gif", "bmp"].includes(fileExt(d?.name)));

export const fmtSize = (bytes) => {
  const b = Number(bytes) || 0;
  if (b > 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  if (b > 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
};
